<template>
     <div class="flex flex-col mx-auto mt-8">
        <div v-if="hasRedirectUrl" class="w-full px-4 pt-5 pb-4 mx-auto overflow-hidden text-center bg-white border-2 shadow-xl border-brand-green rounded-xl sm:mt-8 sm:max-w-xl lg:max-w-3xl sm:p-6">
            <h3 class="text-lg font-medium leading-6 text-white lg:text-2xl" id="modal-title">{{ previousRequestText.notice }}</h3>
            <div class="mt-2">
                <p class="text-sm text-neutral-500 lg:text-lg">{{ previousRequestText.body }}</p>
            </div>
            <div class="mt-5 sm:mt-4">
                <CallToActionButton class="w-full text-white" :href="redirectUrl" :text="previousRequestText.button"></CallToActionButton>
            </div>
        </div>
        <div
            v-else
            class="w-full max-w-3xl mx-auto">
            <div class="md:flex w-full rounded-xl">
                <div class="w-full">
                    <shared-intake-form
                        ref="intakeForm"
                        :config="liveConfig"
                        :preferred-tenant="preferredTenant"
                        @intake-completed="handleSubmission"
                        @client-privacy-clicked="handlePrivacyModalClick"
                        @client-terms-clicked="handlePrivacyModalClick"
                        :split="split"
                    />
                    <intake-conversion
                        v-if="submitting"
                        :form="formData"
                        :logo-src="logoSrc"
                        :page-location="pageLocation"
                        @error="handleError"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { get, mergeWith, cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';
import CallToActionButton from '../CallToActionButton.vue';
import IntakeConversion from "../LeadCapture/IntakeConversion.vue";

/**
 * mergeWith customizer that replaces arrays wholesale instead of merging them
 * by index (lodash's default). Without this, an override of `messages: []` (or
 * any shorter array) leaves the original entries in place because there are no
 * source indices to overwrite. The destination array is emptied and refilled in
 * place so references the plugin captured at mount still observe the new value.
 *
 * @param {*} objValue
 * @param {*} srcValue
 * @returns {Array|undefined}
 */
const replaceArrays = (objValue, srcValue) => {
    if (Array.isArray(srcValue)) {
        if (Array.isArray(objValue)) {
            objValue.length = 0;
            objValue.push(...srcValue);

            return objValue;
        }

        return cloneDeep(srcValue);
    }

    return undefined;
};


export default {
    name: 'IntakeCapture',

    components: {
        CallToActionButton,
        IntakeConversion,
    },

    data() {
        return {
            issueError: null,
            submitting: false,
            formData: null,
            // Cloned config the plugin reads from; mutated in place by VWO overrides.
            liveConfig: cloneDeep(this.config),
        };
    },

    props: {
        logoSrc: {
            type: String,
            required: true
        },
        pageLocation: {
            type: String,
            default: ''
        },
        config: {
            type: Object,
            required: true
        },
        split: {
            type: String,
            default: ''
        }
    },

    methods: {
        ucwords(string) {
            if (!string) return '';
            let words = string.split(' ');
            return words.map((word) => word[0].toUpperCase() + word.substring(1)).join(' ');
        },
        handleSubmission(formData) {
            this.formData = formData;
            this.submitting = true;
        },

        handleError(error) {
            this.submitting = false;
            this.$refs.intakeForm.onSubmissionFailure(error.message, error.canCorrect);
        },

        handlePrivacyModalClick(event) {
            this.$event.$emit('policy-modal:show', event.type);
        },

        /**
         * Merge the VWO override (window.vwoIntakeConfigOverride) into liveConfig in
         * place. The merge must mutate in place because the plugin captures nested
         * references at mount and reads them lazily per module; reassigning liveConfig
         * would not reach it. Override must be a plain object or it is ignored.
         *
         * Only works for values the plugin reads lazily (e.g. module messages). Values
         * consumed once at plugin construction (e.g. system.intake_version) are already
         * read before this runs and cannot be overridden this way.
         *
         * @returns {void}
         */
        applyExperimentOverride() {
            const override = window.vwoIntakeConfigOverride;

            if (override === undefined || override === null) {
                return;
            }

            if (typeof override !== 'object' || Array.isArray(override)) {
                console.warn('[ULH] [IntakeCapture] Ignoring vwoIntakeConfigOverride: expected a plain object', {
                    override_type: Object.prototype.toString.call(override),
                });

                return;
            }

            mergeWith(this.liveConfig, override, replaceArrays);
        },
    },
    computed: {

        /**
         * The firm the visitor chose, or null. The plugin reads this prop through a
         * getter rather than capturing it at mount, so a visitor who starts the intake
         * and only then picks a firm from the directory still has that choice used.
         */
        ...mapGetters('lead', [
            'preferredTenant',
            'redirectUrl',
        ]),

        /**
         * @returns {boolean}
         */
        hasRedirectUrl() {
            return Boolean(this.redirectUrl);
        },

        /**
         * @returns {string|null}
         */
        locationRegion() {
            let region = '';

            if (get(window.SHARED, 'location_region')) {
                region += 'in ' + get(window.SHARED, 'location_region');
            }

            if (get(window.SHARED, 'location_state')) {
                region += ', ' + get(window.SHARED, 'location_state');
            }

            return region;
        },

        /**
         * @returns {string}
         */
        sector() {
            const sector = get(window.SHARED, 'sector');

            if (['disorderly conduct', 'extortion', 'manslaughter', 'public intoxication'].includes(sector)) {
                return 'Criminal Defense';
            }

            return sector
                ? (sector === 'dui'
                    ? 'DUI'
                    : this.ucwords(sector))
                : '';
        },
    },
    beforeMount() {
        const oldSplitVar = localStorage.getItem('alSplitVar')
        if (oldSplitVar !== this.split) {
            localStorage.removeItem('alIntakeData');
            localStorage.setItem('alSplitVar', this.split);
        }
    },
    mounted() {
        this.applyOverrideHandler = () => this.applyExperimentOverride();
        // Apply for a cached/synchronous assignment, then again when VWO applies async.
        this.applyExperimentOverride();
        (window.VWO = window.VWO || []).push(['onVariationApplied', this.applyOverrideHandler]);
        // Hook VWO variation JS (or local testing) can call directly.
        window.__applyIntakeConfigOverride = this.applyOverrideHandler;
    },
    beforeUnmount() {
        if (window.__applyIntakeConfigOverride === this.applyOverrideHandler) {
            delete window.__applyIntakeConfigOverride;
        }
    }
};
</script>


<style scoped>
.intake-capture-wrapper {
    font-family: inherit;
}

/* Ensure plugin components don't overflow the container */
:deep(.intake-form-root) {
    max-width: 100%;
    overflow: hidden;
}

:deep(.message-list) {
    max-width: 100%;
    overflow-x: hidden;
}

:deep(.conversational-input) {
    max-width: 100%;
    box-sizing: border-box;
}

/* Prevent any absolute positioned elements from overflowing */
:deep(.intake-form-wrapper) {
    position: relative;
    overflow: hidden;
    max-width: 100%;
}
</style>
